// ======================================================
// 🌐 BACKEND BASE URL
// ======================================================
const BASE_URL = "https://asaa-0p6p.onrender.com/api";

// ======================================================
// 🔐 AUTH FETCH (Auth0 → Django)
// ======================================================
async function authFetch(url, options = {}, auth0) {
  const { getAccessTokenSilently, isAuthenticated } = auth0;

  if (!isAuthenticated) {
    throw new Error("User not authenticated");
  }

  // ✅ Always request token for YOUR API audience
  const token = await getAccessTokenSilently({
    authorizationParams: {
      audience: "https://ane-crochet-api",
    },
  });

  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  // ❌ Handle backend errors clearly
  if (!res.ok) {
    let errorData;
    try {
      errorData = await res.json();
    } catch {
      errorData = await res.text();
    }
    console.error("❌ API ERROR:", res.status, errorData);
    throw errorData;
  }

  // 204 No Content safety
  if (res.status === 204) return null;

  return res.json();
}

// ======================================================
// 🧺 PRODUCTS (PUBLIC)
// ======================================================
export async function getProducts() {
  const res = await fetch(`${BASE_URL}/products/`);
  return res.json();
}

export async function getProduct(slug) {
  const res = await fetch(`${BASE_URL}/products/${slug}/`);
  return res.json();
}

export async function getTrendingProducts() {
  const res = await fetch(`${BASE_URL}/products/trending/`);
  return res.json();
}

// ======================================================
// 📍 ADDRESS API (FIXED + COMPLETE)
// ======================================================
export function addressAPI(auth0) {
  return {
    // 📥 Get all addresses
    getAddresses() {
      return authFetch(`${BASE_URL}/addresses/`, {}, auth0);
    },

    // ➕ Create address
    createAddress(data) {
      return authFetch(
        `${BASE_URL}/addresses/`,
        {
          method: "POST",
          body: JSON.stringify({
            name: data.name,
            phone: data.phone,
            street: data.street,
            city: data.city,
            pincode: data.pincode,
            address_type: data.address_type || "home", // ✅ REQUIRED
          }),
        },
        auth0
      );
    },

    // ✏️ Update address
    updateAddress(id, data) {
      return authFetch(
        `${BASE_URL}/addresses/${id}/`,
        {
          method: "PUT",
          body: JSON.stringify({
            name: data.name,
            phone: data.phone,
            street: data.street,
            city: data.city,
            pincode: data.pincode,
            address_type: data.address_type,
          }),
        },
        auth0
      );
    },

    // 🗑 Delete address
    deleteAddress(id) {
      return authFetch(
        `${BASE_URL}/addresses/${id}/`,
        {
          method: "DELETE",
        },
        auth0
      );
    },
  };
}

// ======================================================
// ❤️ WISHLIST API
// ======================================================
export function wishlistAPI(auth0) {
  return {
    getWishlist() {
      return authFetch(`${BASE_URL}/wishlist/`, {}, auth0);
    },

    toggleWishlist(product_id) {
      return authFetch(
        `${BASE_URL}/wishlist/`,
        {
          method: "POST",
          body: JSON.stringify({ product_id }),
        },
        auth0
      );
    },
  };
}

// ======================================================
// 🧾 ORDER API
// ======================================================
export function orderAPI(auth0) {
  return {
    placeOrder(data) {
      return authFetch(
        `${BASE_URL}/orders/place/`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        auth0
      );
    },

    getOrderHistory() {
      return authFetch(`${BASE_URL}/orders/history/`, {}, auth0);
    },
  };
}

// ======================================================
// 💳 RAZORPAY API
// ======================================================
export function razorpayAPI(auth0) {
  return {
    createOrder(amount) {
      return authFetch(
        `${BASE_URL}/payments/razorpay/create/`,
        {
          method: "POST",
          body: JSON.stringify({ amount }),
        },
        auth0
      );
    },

    verifyPayment(data) {
      return authFetch(
        `${BASE_URL}/payments/razorpay/verify/`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        auth0
      );
    },
  };
}
